import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { CalendarRange, Check, X, Clock, Plus } from "lucide-react";

const TYPE_LABEL = {
  holiday: "Ferie",
  permission: "Permesso",
  rol: "ROL",
  law_104: "Legge 104",
  parental_leave: "Congedo parentale",
  medical_visit: "Visita medica",
  union: "Sindacale",
  illness: "Malattia",
  other: "Altro",
};

const LEAVE_TYPES = Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label }));

const STATUS = [
  { id: "pending", label: "In attesa", icon: Clock, tone: "text-amber-700 bg-amber-50 border-amber-200" },
  { id: "approved", label: "Approvate", icon: Check, tone: "text-green-700 bg-green-50 border-green-200" },
  { id: "rejected", label: "Rifiutate", icon: X, tone: "text-red-700 bg-red-50 border-red-200" },
];

const statusPill = (s) => {
  const cfg = { pending: "bg-amber-100 text-amber-700", approved: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700" };
  const label = { pending: "In attesa", approved: "Approvata", rejected: "Rifiutata" };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest ${cfg[s] || "bg-gray-100"}`}>{label[s] || s}</span>;
};

export default function LeavesPage() {
  const [filter, setFilter] = useState("pending");
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState({});
  const [employeesList, setEmployeesList] = useState([]);
  const [decideFor, setDecideFor] = useState(null);
  const [decision, setDecision] = useState("approved");
  const [note, setNote] = useState("");
  const [me, setMe] = useState(null);

  // Form richiesta nuova
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({
    employee_id: "",
    type: "holiday",
    start_date: "",
    end_date: "",
    hours: "",
    notes: "",
    auto_approve: false,
  });

  const load = async () => {
    const params = filter && filter !== "all" ? `?status=${filter}` : "";
    const [l, e] = await Promise.all([api.get(`/leaves${params}`), api.get("/employees")]);
    setLeaves(l.data);
    setEmployeesList(e.data);
    const map = {};
    e.data.forEach((x) => { map[x.id] = x; });
    setEmployees(map);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const isAdminLike = useMemo(() => {
    const role = me?.role;
    return role === "admin" || role === "sub_admin" || role === "superadmin";
  }, [me]);

  const openNew = () => {
    // Default: se admin è collegato come dipendente, preseleziona se stesso
    const defaultEmp = me?.employee_id || employeesList[0]?.id || "";
    setForm({
      employee_id: defaultEmp,
      type: "holiday",
      start_date: "",
      end_date: "",
      hours: "",
      notes: "",
      auto_approve: false,
    });
    setNewOpen(true);
  };

  const openDecide = (l, status) => { setDecideFor(l); setDecision(status); setNote(""); };
  const submitDecision = async () => {
    try {
      await api.patch(`/leaves/${decideFor.id}/decision`, { status: decision, decision_note: note });
      toast.success(decision === "approved" ? "Richiesta approvata" : "Richiesta rifiutata");
      setDecideFor(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const submitNew = async () => {
    if (!form.employee_id) { toast.error("Seleziona un dipendente"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Date obbligatorie"); return; }
    if (form.end_date < form.start_date) { toast.error("Data fine precedente a inizio"); return; }
    try {
      const payload = {
        employee_id: form.employee_id,
        type: form.type,
        start_date: form.start_date,
        end_date: form.end_date,
        hours: form.hours ? parseFloat(form.hours) : null,
        notes: form.notes,
        auto_approve: !!form.auto_approve,
      };
      await api.post("/leaves", payload);
      toast.success(form.auto_approve ? "Richiesta creata e approvata" : "Richiesta creata");
      setNewOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore invio richiesta"); }
  };

  const empName = (eid) => {
    const e = employees[eid];
    return e ? `${e.name} ${e.lastname}` : eid?.slice(0, 8);
  };
  const empJob = (eid) => employees[eid]?.job || "—";

  return (
    <div className="p-4 md:p-12" data-testid="leaves-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Risorse Umane</div>
          <h1 className="font-display text-4xl">Ferie & Permessi</h1>
          <p className="text-[#555] mt-1 text-sm">Gestisci le richieste dei dipendenti · approva o rifiuta</p>
        </div>
        {isAdminLike && (
          <Dialog open={newOpen} onOpenChange={setNewOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-leave-request-btn" className="rounded-full bg-[#FF7A29] hover:bg-black h-11 btn-glow text-white">
                <Plus className="w-4 h-4 mr-2"/> Nuova richiesta
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl border-0 max-w-lg" data-testid="new-leave-dialog">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Nuova richiesta ferie/permesso</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label>Dipendente</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="new-leave-employee"><SelectValue placeholder="Seleziona dipendente"/></SelectTrigger>
                    <SelectContent>
                      {employeesList.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} {e.lastname}{me?.employee_id === e.id ? " (Me)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Tipo</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="new-leave-type"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {LEAVE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Dal</Label>
                  <Input type="date" className="rounded-xl mt-1" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="new-leave-start"/>
                </div>
                <div>
                  <Label>Al</Label>
                  <Input type="date" className="rounded-xl mt-1" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="new-leave-end"/>
                </div>
                <div className="sm:col-span-2">
                  <Label>Ore (opzionale, per permessi parziali)</Label>
                  <Input type="number" step="0.5" className="rounded-xl mt-1" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="Es. 4"/>
                </div>
                <div className="sm:col-span-2">
                  <Label>Note</Label>
                  <Input className="rounded-xl mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Motivazione o dettagli aggiuntivi"/>
                </div>
                <div className="sm:col-span-2 flex items-center gap-2 mt-1">
                  <Checkbox
                    id="auto-approve"
                    checked={form.auto_approve}
                    onCheckedChange={(v) => setForm({ ...form, auto_approve: !!v })}
                    data-testid="new-leave-auto-approve"
                  />
                  <label htmlFor="auto-approve" className="text-sm cursor-pointer">
                    Inserisci già come <strong>Approvata</strong> (utile per registrare richieste verbali o pregresse)
                  </label>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submitNew} data-testid="new-leave-submit" className="rounded-full bg-[#FF7A29] hover:bg-black text-white">Salva richiesta</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {[{id:"all",label:"Tutte",icon:CalendarRange,tone:""}, ...STATUS].map((s) => {
          const Icon = s.icon;
          const active = filter === s.id;
          return (
            <button key={s.id} onClick={()=>setFilter(s.id)} data-testid={`leaves-filter-${s.id}`}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 transition-all ${active ? "bg-[#FF7A29] text-white shadow-lg shadow-orange-500/30" : "bg-white border border-[#EDE5D8] hover:border-[#FF7A29]"}`}>
              <Icon className="w-4 h-4"/> {s.label}
            </button>
          );
        })}
      </div>

      <div className="card-smart overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-[#EDE5D8]"><tr className="text-left">
            <th className="p-4">Dipendente</th><th className="p-4">Tipo</th><th className="p-4">Periodo</th><th className="p-4">Ore</th><th className="p-4">Note</th><th className="p-4">Stato</th><th className="p-4"></th>
          </tr></thead>
          <tbody>
            {leaves.length === 0 && <tr><td colSpan="7" className="p-12 text-center text-[#757575]"><CalendarRange className="w-10 h-10 mx-auto mb-3 text-[#FF7A29]/50"/>Nessuna richiesta con questo filtro.</td></tr>}
            {leaves.map((l) => (
              <tr key={l.id} className="border-b border-[#EDE5D8] hover:bg-[#FFF0E3]/50" data-testid={`leave-${l.id}`}>
                <td className="p-4"><div className="font-semibold">{empName(l.employee_id)}</div><div className="text-xs text-[#757575]">{empJob(l.employee_id)}</div></td>
                <td className="p-4 font-semibold">{TYPE_LABEL[l.type] || l.type}</td>
                <td className="p-4 text-xs">{l.start_date} <span className="text-[#757575]">→</span> {l.end_date}</td>
                <td className="p-4 text-xs">{l.hours ?? "—"}</td>
                <td className="p-4 text-xs text-[#757575]">
                  {l.notes}
                  {l.decision_note && <div className="text-[10px] italic mt-1">Risposta: {l.decision_note}</div>}
                </td>
                <td className="p-4">{statusPill(l.status)}</td>
                <td className="p-4 text-right">
                  {l.status === "pending" && (
                    <>
                      <Button size="icon" onClick={()=>openDecide(l, "approved")} data-testid={`approve-${l.id}`} className="bg-green-600 hover:bg-green-700 rounded-full h-8 w-8 mr-1"><Check className="w-4 h-4"/></Button>
                      <Button size="icon" onClick={()=>openDecide(l, "rejected")} data-testid={`reject-${l.id}`} className="bg-red-600 hover:bg-red-700 rounded-full h-8 w-8"><X className="w-4 h-4"/></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!decideFor} onOpenChange={(v)=>{ if(!v) setDecideFor(null); }}>
        <DialogContent className="rounded-3xl border-0 max-w-md" data-testid="leave-decide-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{decision === "approved" ? "Approva richiesta" : "Rifiuta richiesta"}</DialogTitle></DialogHeader>
          {decideFor && (
            <div className="space-y-4">
              <div className="text-sm bg-[#FFF0E3]/50 border border-[#EDE5D8] rounded-xl p-3">
                <div className="font-semibold">{empName(decideFor.employee_id)} · {TYPE_LABEL[decideFor.type]}</div>
                <div className="text-xs text-[#757575]">{decideFor.start_date} → {decideFor.end_date}</div>
                {decideFor.notes && <div className="text-xs mt-1">{decideFor.notes}</div>}
              </div>
              <div><Label>Nota per il dipendente (opzionale)</Label><Input data-testid="decision-note" className="rounded-xl mt-1" value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Es: Approvato, ricordati di lasciare il pc in ufficio"/></div>
              <DialogFooter>
                <Button onClick={submitDecision} data-testid="decision-submit" className={`rounded-full text-white ${decision === "approved" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}>
                  Conferma {decision === "approved" ? "approvazione" : "rifiuto"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
